import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bot, Loader2, MessageCircle, Send, X } from 'lucide-react';
import { API_URL } from '../utils/api';

const TOKEN_KEY = 'stikes-chat-token';
// Menandai sapaan otomatis (teaser) sudah ditutup/dibuka pada sesi browser ini
// supaya tidak muncul berulang kali mengganggu pengunjung.
const TEASER_KEY = 'stikes-chat-teaser-dismissed';
const DEFAULT_PERSONA = { name: 'Asisten AI', greeting: '', teaserEnabled: false, teaserDelaySeconds: 5 };

// Path halaman publik yang sungguh ada — HARUS disinkronkan manual dengan
// SITE_PATHS di backend/src/routes/chat.js (sistem prompt model memakai
// daftar yang sama, supaya path yang disebut model selalu valid untuk
// ditautkan di sini).
const SITE_PATHS = [
  '/program-studi', '/beasiswa', '/kalender', '/sarana-prasarana', '/ukm', '/dosen-tendik',
  '/berita', '/galeri', '/agenda', '/download', '/tracer-study', '/struktur-organisasi',
  '/unit/pusat-karir', '/unit/baak', '/unit/bak', '/unit/bauk', '/unit/kemahasiswaan',
  '/unit/humas', '/unit/it', '/kontak',
];
const ESCAPED_PATHS = [...SITE_PATHS].sort((a, b) => b.length - a.length)
  .map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
// URL lengkap situs kampus lain (mis. https://it.stikes-hi.ac.id/pengaduan,
// https://pmb.stikes-hi.ac.id) juga dijadikan tautan; domain luar tidak.
const CAMPUS_URL = String.raw`https?:\/\/(?:[\w-]+\.)*stikes-hi\.ac\.id(?![\w-]|\.[\w-])(?:\/[^\s<>"'()]*)?`;
const INLINE_PATTERN = new RegExp(
  `\\*\\*(.+?)\\*\\*|(${CAMPUS_URL})|(?<![\\w/-])(${ESCAPED_PATHS.join('|')})(?![\\w/-])`,
  'g',
);

/// Memecah satu baris jadi teks/**tebal**/tautan halaman situs (path dari
/// SITE_PATHS otomatis jadi <Link> yang bisa diklik; URL *.stikes-hi.ac.id
/// jadi tautan tab baru agar percakapan di widget tidak hilang).
function renderInline(text, keyPrefix) {
  const nodes = [];
  let lastIndex = 0;
  let match;
  let i = 0;
  INLINE_PATTERN.lastIndex = 0;
  while ((match = INLINE_PATTERN.exec(text))) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    let end = INLINE_PATTERN.lastIndex;
    if (match[1] !== undefined) {
      nodes.push(<strong key={`${keyPrefix}-b-${i++}`}>{match[1]}</strong>);
    } else if (match[2] !== undefined) {
      // Tanda baca penutup kalimat bukan bagian dari URL.
      const url = match[2].replace(/[.,;:!?]+$/, '');
      end = match.index + url.length;
      nodes.push(
        <a
          key={`${keyPrefix}-u-${i++}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="break-all font-medium underline underline-offset-2 hover:text-[#7B2FBE] dark:hover:text-purple-300"
        >
          {url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
        </a>,
      );
    } else if (match[3] !== undefined) {
      nodes.push(
        <Link
          key={`${keyPrefix}-l-${i++}`}
          to={match[3]}
          className="font-medium underline underline-offset-2 hover:text-[#7B2FBE] dark:hover:text-purple-300"
        >
          {match[3]}
        </Link>,
      );
    }
    lastIndex = end;
    INLINE_PATTERN.lastIndex = end;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

/// Merapikan balasan model — memecah jadi baris/paragraf, mengenali bullet
/// ("- "/"• ") jadi daftar, **tebal** jadi <strong>, dan path halaman situs
/// jadi tautan — tanpa perlu library markdown penuh (balasan model selalu
/// singkat, tak butuh heading/tabel/dll).
function renderMessage(text) {
  const lines = text.split('\n');
  const blocks = [];
  let bulletBuffer = [];
  const flushBullets = () => {
    if (!bulletBuffer.length) return;
    blocks.push(
      <ul key={`ul-${blocks.length}`} className="my-1 list-disc space-y-0.5 pl-4">
        {bulletBuffer.map((line, i) => <li key={i}>{renderInline(line, `b${i}`)}</li>)}
      </ul>,
    );
    bulletBuffer = [];
  };

  lines.forEach((line, i) => {
    const bullet = /^\s*[-•]\s+(.*)/.exec(line);
    if (bullet) {
      bulletBuffer.push(bullet[1]);
      return;
    }
    flushBullets();
    if (line.trim() === '') {
      blocks.push(<div key={`sp-${i}`} className="h-2" />);
    } else {
      blocks.push(<p key={`p-${i}`} className="[&:not(:first-child)]:mt-1">{renderInline(line, `p${i}`)}</p>);
    }
  });
  flushBullets();
  return blocks;
}

/// Avatar persona asisten — dipakai konsisten di header, sapaan, dan balasan
/// supaya pengunjung merasa mengobrol dengan satu sosok "resepsionis".
function AssistantAvatar({ size = 16, box = 'h-8 w-8' }) {
  return (
    <span className={`grid shrink-0 place-items-center rounded-full bg-gradient-to-br from-[#7B2FBE] to-[#9333EA] text-white ${box}`}>
      <Bot size={size} />
    </span>
  );
}

/// Form singkat sebelum bisa mengobrol — nama & WhatsApp wajib supaya staf
/// bisa menindaklanjuti, pertanyaan awal langsung jadi pesan pertama
/// (tak ada kolom "keperluan" terpisah, biar tetap ringan diisi).
function IntakeForm({ busy, error, greeting, assistantName, onSubmit }) {
  const [name, setName] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [message, setMessage] = useState('');

  const submit = (event) => {
    event.preventDefault();
    if (!name.trim() || !whatsapp.trim() || !message.trim() || busy) return;
    onSubmit({ name: name.trim(), whatsapp: whatsapp.trim(), message: message.trim() });
  };

  return (
    <form onSubmit={submit} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
      {greeting && (
        <div className="flex items-start gap-2.5 rounded-xl bg-gray-50 px-3 py-2.5 dark:bg-white/5">
          <AssistantAvatar size={13} box="h-7 w-7" />
          <div>
            <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-200">{assistantName}</p>
            <p className="text-xs leading-relaxed text-gray-500 dark:text-gray-400">{greeting}</p>
          </div>
        </div>
      )}
      <p className="text-xs leading-relaxed text-gray-500 dark:text-gray-400">
        Isi data singkat ini dulu ya, supaya tim kami bisa menghubungi Anda kembali bila diperlukan.
      </p>
      <label className="block">
        <span className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">Nama</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          required
          placeholder="Nama Anda"
          className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm outline-none transition focus:border-[#7B2FBE] focus:bg-white dark:border-white/10 dark:bg-[#100e1a] dark:text-white"
        />
      </label>
      <label className="block">
        <span className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">Nomor WhatsApp</span>
        <input
          value={whatsapp}
          onChange={(e) => setWhatsapp(e.target.value)}
          maxLength={20}
          required
          inputMode="tel"
          placeholder="08xxxxxxxxxx"
          className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm outline-none transition focus:border-[#7B2FBE] focus:bg-white dark:border-white/10 dark:bg-[#100e1a] dark:text-white"
        />
      </label>
      <label className="block">
        <span className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">Apa yang ingin Anda tanyakan?</span>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={500}
          required
          rows={3}
          placeholder="mis. Info pendaftaran, program studi, atau layanan kampus"
          className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm outline-none transition focus:border-[#7B2FBE] focus:bg-white dark:border-white/10 dark:bg-[#100e1a] dark:text-white"
        />
      </label>
      {error && <p className="text-xs text-red-500">{error}</p>}
      <button
        type="submit"
        disabled={busy || !name.trim() || !whatsapp.trim() || !message.trim()}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#7B2FBE] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#9333EA] disabled:opacity-50"
      >
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
        {busy ? 'Mengirim…' : 'Mulai Chat'}
      </button>
      <p className="text-center text-[10px] text-gray-400">
        Data ini hanya dipakai untuk komunikasi terkait pertanyaan Anda ke STIKES Harapan Ibu Jambi.
      </p>
    </form>
  );
}

/// Widget chat publik — hanya membalas topik kampus (dibatasi di system
/// prompt sisi server). Muncul hanya bila diaktifkan dari panel admin (menu
/// Asisten AI). Pengunjung wajib mengisi nama + WhatsApp + pertanyaan awal
/// sekali per sesi browser (token disimpan sessionStorage) sebelum bisa
/// mengobrol — bukan cuma anti-spam, tapi juga supaya tim PMB bisa
/// menindaklanjuti lewat WhatsApp.
const ChatWidget = () => {
  const [available, setAvailable] = useState(false);
  const [persona, setPersona] = useState(DEFAULT_PERSONA);
  const [teaserOpen, setTeaserOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState('intake'); // 'intake' | 'chat'
  const [token, setToken] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const listRef = useRef(null);

  useEffect(() => {
    let active = true;
    fetch(`${API_URL}/api/public/chat/status`)
      .then((res) => (res.ok ? res.json() : { enabled: false }))
      .then((data) => {
        if (!active) return;
        setAvailable(Boolean(data.enabled));
        if (!data.enabled) return;
        setPersona({
          name: data.name?.trim() || DEFAULT_PERSONA.name,
          greeting: data.greeting?.trim() || '',
          teaserEnabled: Boolean(data.teaserEnabled),
          teaserDelaySeconds: Number.isFinite(data.teaserDelaySeconds)
            ? data.teaserDelaySeconds
            : DEFAULT_PERSONA.teaserDelaySeconds,
        });
      })
      .catch(() => { if (active) setAvailable(false); });
    return () => { active = false; };
  }, []);

  // Lanjutkan sesi yang sudah ada (mis. widget ditutup lalu dibuka lagi
  // dalam tab yang sama) — tanpa ini pengunjung harus isi form berulang.
  useEffect(() => {
    let active = true;
    const saved = sessionStorage.getItem(TOKEN_KEY);
    if (!saved) return undefined;
    fetch(`${API_URL}/api/public/chat/history?token=${encodeURIComponent(saved)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!active || !data) { sessionStorage.removeItem(TOKEN_KEY); return; }
        setToken(saved);
        setMessages(data.messages || []);
        setPhase('chat');
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending, open, phase]);

  // Sapaan otomatis ala resepsionis: muncul sekali setelah jeda yang diatur
  // admin, hanya bila widget belum dibuka, pengunjung belum pernah mengobrol,
  // dan sapaan belum ditutup/dibuka sebelumnya pada sesi browser ini.
  useEffect(() => {
    if (!available || !persona.teaserEnabled || open || phase !== 'intake') return undefined;
    if (sessionStorage.getItem(TEASER_KEY) || sessionStorage.getItem(TOKEN_KEY)) return undefined;
    const delay = Math.max(0, persona.teaserDelaySeconds || 0) * 1000;
    const timer = setTimeout(() => setTeaserOpen(true), delay);
    return () => clearTimeout(timer);
  }, [available, persona.teaserEnabled, persona.teaserDelaySeconds, open, phase]);

  function dismissTeaser() {
    sessionStorage.setItem(TEASER_KEY, '1');
    setTeaserOpen(false);
  }

  function openChat() {
    dismissTeaser();
    setOpen(true);
  }

  async function startChat({ name, whatsapp, message }) {
    setSending(true); setError('');
    let res;
    try {
      res = await fetch(`${API_URL}/api/public/chat/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, whatsapp, message }),
      });
    } catch {
      setError('Tidak bisa terhubung ke server. Periksa koneksi internet Anda dan coba lagi.');
      setSending(false);
      return;
    }
    try {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Asisten AI sedang bermasalah, coba lagi nanti.');
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setMessages([{ role: 'user', content: message }, { role: 'assistant', content: data.reply }]);
      setPhase('chat');
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  }

  async function sendMessage(event) {
    event.preventDefault();
    const text = input.trim();
    if (!text || sending || !token) return;

    setMessages((cur) => [...cur, { role: 'user', content: text }]);
    setInput('');
    setSending(true);
    setError('');

    let res;
    try {
      res = await fetch(`${API_URL}/api/public/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, message: text }),
      });
    } catch {
      setError('Tidak bisa terhubung ke server. Periksa koneksi internet Anda dan coba lagi.');
      setSending(false);
      return;
    }

    try {
      const data = await res.json().catch(() => ({}));
      if (res.status === 404) {
        // Sesi lama sudah tak ada (mis. terhapus retensi) — mulai ulang.
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setMessages([]);
        setPhase('intake');
        throw new Error('Sesi chat sebelumnya sudah berakhir. Silakan isi ulang data untuk memulai percakapan baru.');
      }
      if (!res.ok) throw new Error(data.error || 'Asisten AI sedang bermasalah, coba lagi nanti.');
      setMessages((cur) => [...cur, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  }

  if (!available) return null;

  return (
    <div className="fixed bottom-24 right-6 z-50 flex flex-col items-end gap-3">
      {open && (
        <div className="flex h-[28rem] w-[22rem] max-w-[calc(100vw-3rem)] flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl dark:border-white/10 dark:bg-[#171528]">
          <div className="flex items-center justify-between gap-2 bg-gradient-to-r from-[#7B2FBE] to-[#9333EA] px-4 py-3 text-white">
            <div className="flex items-center gap-2.5">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/15">
                <Bot size={18} />
              </span>
              <div>
                <p className="text-sm font-bold leading-tight">{persona.name}</p>
                <p className="flex items-center gap-1.5 text-[11px] leading-tight text-white/75">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden="true" />
                  Resepsionis digital · Online
                </p>
              </div>
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Tutup chat" className="rounded-lg p-1 hover:bg-white/15">
              <X size={16} />
            </button>
          </div>

          {phase === 'intake' ? (
            <IntakeForm busy={sending} error={error} greeting={persona.greeting} assistantName={persona.name} onSubmit={startChat} />
          ) : (
            <>
              <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
                {messages.map((m, i) => (
                  <div key={i} className={`flex items-end gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {m.role === 'assistant' && <AssistantAvatar size={12} box="h-6 w-6" />}
                    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                      m.role === 'user'
                        ? 'bg-[#7B2FBE] text-white'
                        : 'bg-gray-100 text-gray-800 dark:bg-white/10 dark:text-gray-100'
                    }`}>
                      {m.role === 'assistant' && (
                        <p className="mb-0.5 text-[10px] font-semibold text-gray-400 dark:text-gray-500">{persona.name}</p>
                      )}
                      {m.role === 'assistant' ? renderMessage(m.content) : m.content}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex items-end justify-start gap-2">
                    <AssistantAvatar size={12} box="h-6 w-6" />
                    <div className="flex items-center gap-1.5 rounded-2xl bg-gray-100 px-3.5 py-2 text-xs text-gray-500 dark:bg-white/10 dark:text-gray-400">
                      <Loader2 size={13} className="animate-spin" /> Mengetik…
                    </div>
                  </div>
                )}
                {error && (
                  <p className="text-xs text-red-500">{error}</p>
                )}
              </div>

              <div className="border-t border-gray-100 px-3 py-2 text-[10px] text-gray-400 dark:border-white/10">
                Jawaban dari AI, untuk info resmi hubungi{' '}
                <Link to="/kontak" onClick={() => setOpen(false)} className="font-semibold text-[#7B2FBE] hover:underline dark:text-purple-300">
                  halaman Kontak
                </Link>.
              </div>

              <form onSubmit={sendMessage} className="flex items-center gap-2 border-t border-gray-100 p-3 dark:border-white/10">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Tulis pertanyaan…"
                  maxLength={500}
                  disabled={sending}
                  className="flex-1 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm outline-none transition focus:border-[#7B2FBE] focus:bg-white dark:border-white/10 dark:bg-[#100e1a] dark:text-white"
                />
                <button
                  type="submit"
                  disabled={sending || !input.trim()}
                  aria-label="Kirim"
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#7B2FBE] text-white transition hover:bg-[#9333EA] disabled:opacity-50"
                >
                  <Send size={15} />
                </button>
              </form>
            </>
          )}
        </div>
      )}

      {teaserOpen && !open && (
        <div className="w-[19rem] max-w-[calc(100vw-3rem)] rounded-2xl border border-gray-100 bg-white p-3.5 shadow-2xl dark:border-white/10 dark:bg-[#171528]">
          <div className="flex items-start gap-2.5">
            <AssistantAvatar size={15} box="h-9 w-9" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-gray-900 dark:text-white">{persona.name}</p>
              <p className="mt-0.5 text-xs leading-relaxed text-gray-600 dark:text-gray-300">{persona.greeting}</p>
            </div>
            <button
              type="button"
              onClick={dismissTeaser}
              aria-label="Tutup sapaan"
              className="rounded-lg p-1 text-gray-400 transition hover:bg-gray-100 dark:hover:bg-white/10"
            >
              <X size={14} />
            </button>
          </div>
          <button
            type="button"
            onClick={openChat}
            className="mt-3 w-full rounded-xl bg-[#7B2FBE] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#9333EA]"
          >
            Mulai chat sekarang
          </button>
        </div>
      )}

      <div className="relative">
        <span className="pointer-events-none absolute inset-0 rounded-full bg-[#7B2FBE] wa-pulse opacity-50" aria-hidden="true" />
        <button
          type="button"
          data-testid="chat-ai-float-btn"
          onClick={() => { dismissTeaser(); setOpen((v) => !v); }}
          aria-label={open ? `Tutup ${persona.name}` : `Tanya ${persona.name}`}
          className="group relative grid h-14 w-14 place-items-center rounded-full bg-[#7B2FBE] text-white shadow-xl transition-transform duration-300 hover:scale-110 hover:bg-[#9333EA]"
        >
          {open ? <X size={22} /> : <MessageCircle size={22} />}
          {!open && (
            <span className="pointer-events-none absolute right-16 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1.5 text-xs text-white opacity-0 shadow-xl transition-opacity duration-200 group-hover:opacity-100">
              Tanya {persona.name}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default ChatWidget;
